import React, { useState } from 'react'
import { FieldArray, useFormikContext } from 'formik'
import { useTranslation } from 'react-i18next'

import Alert from '@mui/material/Alert'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'

import AddRoundedIcon from '@mui/icons-material/AddRounded'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded'

import InfoSection from '@/components/ui/sections/InfoSection'

import { parentValueGenerator } from '@/utils/patient'

import ParentDialog from './ParentDialog'
import { PatientProfileFormData } from '@/features/patient/patient-profile-form'

export type ParentsFieldArrayProps = {
  readOnly?: boolean
}

export function ParentsFieldArray(props: ParentsFieldArrayProps) {
  const { readOnly } = props
  const { values } = useFormikContext<PatientProfileFormData>()
  const { t } = useTranslation()

  const [addParentDialogOpen, setAddParentDialogOpen] = useState<boolean>(false)
  const [modifyParentDialogOpen, setModifyParentDialogOpen] = useState<boolean>(false)
  const [selectedParentIndex, setSelectedParentIndex] = useState<number | undefined>()

  const toggleAddParentDialog = () => {
    setAddParentDialogOpen(!addParentDialogOpen)
  }

  const toggleModifyParentDialog = (parentIndex?: number) => () => {
    setSelectedParentIndex(parentIndex)
    setModifyParentDialogOpen(!modifyParentDialogOpen)
  }

  return (
    <FieldArray name="parents">
      {({ push, remove, replace }) => (
        <InfoSection
          action={
            !readOnly && (
              <Tooltip title={t('page.infoPatient.informations.infoSection.parents.addParentButton.tooltip')}>
                <IconButton onClick={toggleAddParentDialog} size="small">
                  <AddRoundedIcon />
                </IconButton>
              </Tooltip>
            )
          }
          label={t('page.infoPatient.informations.infoSection.parents.label')}
        >
          {!values.parents.length && readOnly ? (
            <Alert icon={false} severity="info" variant="outlined" sx={{ mt: '10px' }}>
              {t('page.infoPatient.informations.infoSection.parents.noParentMessage')}
            </Alert>
          ) : (
            <TableContainer component={Paper} sx={{ mt: '16px' }}>
              <Table>
                <TableBody>
                  {values.parents.map((parent, index) => (
                    <TableRow key={index}>
                      <TableCell component="th" scope="row">
                        {`${parent.firstname} ${parent.lastname}`}
                      </TableCell>
                      <TableCell align="right">
                        <Stack display="inline-flex" direction="row" spacing={1}>
                          <Tooltip title={t('common:button.show.tooltip')}>
                            <IconButton onClick={toggleModifyParentDialog(index)} size="small">
                              <VisibilityRoundedIcon fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                          {!readOnly && (
                            <Tooltip title={t('common:button.delete.tooltip')}>
                              <IconButton onClick={() => remove(index)} size="small">
                                <DeleteRoundedIcon fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                      {modifyParentDialogOpen && selectedParentIndex === index && (
                        <ParentDialog
                          initialValues={parent}
                          mode="update"
                          onClose={toggleModifyParentDialog()}
                          onSubmit={(values) => {
                            replace(index, values)
                            toggleModifyParentDialog()
                          }}
                          open
                          readOnly={readOnly}
                        />
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {addParentDialogOpen && (
            <ParentDialog
              initialValues={parentValueGenerator()}
              mode="create"
              onClose={toggleAddParentDialog}
              onSubmit={(values) => {
                push(values)
                toggleAddParentDialog()
              }}
              open
            />
          )}
        </InfoSection>
      )}
    </FieldArray>
  )
}

export default ParentsFieldArray
